/**
 * The AI providers as both sides know them: the browser calls their APIs with a key saved on the device
 * (client/ai/), and a self-hosted server started with AI_PROVIDER and AI_KEY forwards the same calls with its own key
 * (server/routes/ai.ts).
 */

export const AI_PROVIDER_IDS = ['anthropic', 'openai'] as const;
export type AiProviderId = (typeof AI_PROVIDER_IDS)[number];

export const isAiProviderId = (value: unknown): value is AiProviderId => typeof value === 'string' && (AI_PROVIDER_IDS as readonly string[]).includes(value);

/** Where each provider's API is. Its origin is in the Content-Security-Policy's connect-src (csp.ts). */
export const AI_API: Record<AiProviderId, string> = {
  anthropic: 'https://api.anthropic.com/v1',
  openai: 'https://api.openai.com/v1',
};

/** The version of Anthropic's API the requests are written for. */
export const ANTHROPIC_VERSION = '2026-10-21';

/** The model a new setup starts with: Settings › AI's first choice, and the server's without AI_MODEL. */
export const DEFAULT_AI_MODEL: Record<AiProviderId, string> = {
  anthropic: 'claude-sonnet-5-5',
  openai: 'gpt-6-luna',
};

/** What a self-hosted server can ask an OpenAI-compatible server's model at (AI_REASONING_EFFORT): reasoning_effort. */
export const REASONING_EFFORTS = ['none', 'low', 'medium', 'high'] as const;
export type ReasoningEffort = (typeof REASONING_EFFORTS)[number];

/**
 * An OpenAI-compatible server's model (AI_BASE_URL) answers without thinking unless AI_REASONING_EFFORT says otherwise.
 * The app asks for low, but Ollama's thinking models take any level but none as "think", for thousands of tokens,
 * and Ollama ignores the cap on them (max_completion_tokens). In #180's evaluation, qwen3.5:4b on a CPU never finished
 * thinking, and answered in about two and a half minutes without.
 */
export const BASE_URL_EFFORT: ReasoningEffort = 'none';
